import usersIcon from '@iconify/icons-ant-design/user-outline';
import overviewIcon from '@iconify/icons-ic/outline-dashboard';
import { Icon } from '@iconify/react';
import applicationsIcon from '@iconify/icons-ic/outline-settings-applications';
import plusOutline from '@iconify/icons-eva/plus-outline';
import { PATH_APP } from 'routes/paths';
import { PERMISSIONS as P, hasOrganizationPermission } from 'utils/organizationPermissions';
import { isMobeatStaff } from 'utils/mobeatStaff';
import { isMessagingEnabled } from 'utils/messagingAccess';

export const APPLICATIONS_MAP = {
  1: 'Tiny Minies',
  2: 'Sevimli Dostlar',
  3: 'Todly',
};

function filterApplicationRoutes(routes, organizationRole) {
  return routes
    .filter((route) => hasOrganizationPermission(organizationRole, route.permissions))
    .map((route) => {
      if (!route.children?.length) {
        return route;
      }
      const parentPerms = route.permissions;
      const children = route.children.filter((child) =>
        hasOrganizationPermission(organizationRole, child.permissions ?? parentPerms),
      );
      return { ...route, children };
    })
    .filter((route) => !route.children?.length || route.children.length > 0);
}

const MOBEAT_STAFF_ROUTES = [
  {
    title: 'demo requests',
    path: PATH_APP.admin.demoLeads,
    icon: <Icon icon='mdi:inbox-arrow-down' width={20} height={20} />,
    permissions: [P.NAV_DEMO_LEADS],
  },
];

const MANAGEMENT_ROUTES = [
  {
    title: 'overview',
    path: PATH_APP.admin.overview,
    icon: <Icon icon={overviewIcon} width={20} height={20} />,
    permissions: [],
  },
  {
    title: 'users',
    path: PATH_APP.admin.users.root,
    icon: <Icon icon={usersIcon} width={20} height={20} />,
    permissions: [P.ADMIN_CUSTOMER_ADMIN_ONLY],
  },
  {
    title: 'applications',
    path: PATH_APP.admin.applications,
    icon: <Icon icon={applicationsIcon} width={20} height={20} />,
    permissions: [],
  },
  {
    title: 'app setup',
    path: PATH_APP.admin.applicationOnboarding,
    icon: <Icon icon={plusOutline} width={20} height={20} />,
    permissions: [P.ADMIN_CUSTOMER_ADMIN_ONLY],
  },
];

const APPLICATION_ROUTES = (appId) => [
  {
    title: 'overview',
    path: PATH_APP.application.overview(appId),
    icon: <Icon icon={overviewIcon} width={20} height={20} />,
    permissions: [P.NAV_OVERVIEW],
  },
  {
    title: 'users',
    path: PATH_APP.application.user.list(appId),
    icon: <Icon icon='lucide:users' width={20} height={20} />,
    permissions: [P.NAV_USERS],
  },
  {
    title: 'top spenders',
    path: PATH_APP.application.topSpenders(appId),
    icon: <Icon icon='mdi:trophy-outline' width={20} height={20} />,
    permissions: [P.NAV_USERS],
  },
  {
    title: 'transactions',
    path: PATH_APP.application.transaction.root(appId),
    icon: <Icon icon='ep:operation' width={20} height={20} />,
    children: [
      {
        title: 'Transaction List',
        path: PATH_APP.application.transaction.list(appId),
        icon: <Icon icon='ep:list' width={20} height={20} />,
        permissions: [P.NAV_TRANSACTIONS],
      },
      {
        title: 'App Store Lookup',
        path: PATH_APP.application.transaction.appStoreLookup(appId),
        icon: <Icon icon='ep:search' width={20} height={20} />,
        permissions: [P.NAV_TRANSACTIONS],
      },
    ],
    permissions: [P.NAV_TRANSACTIONS],
  },
  {
    title: 'charts',
    path: `/applications/${appId}/charts`,
    icon: <Icon icon='bi:bar-chart-line' width={20} height={20} />,
    permissions: [P.NAV_CHARTS],
    children: [],
  },
  {
    title: 'A/B Tests',
    path: PATH_APP.application.abTest.report(appId),
    icon: <Icon icon='mdi:ab-testing' width={20} height={20} />,
    permissions: [P.NAV_ABTESTS],
  },
  {
    title: 'products',
    path: PATH_APP.application.product.list(appId),
    icon: <Icon icon='mdi:package-variant' width={20} height={20} />,
    permissions: [P.NAV_PRODUCTS],
  },
  isMessagingEnabled(appId) && {
    title: 'mailing & notifications',
    icon: <Icon icon='material-symbols:notifications-active-outline-rounded' width={20} height={20} />,
    permissions: [P.NAV_MAILING],
    children: [
      {
        title: 'lists',
        path: PATH_APP.application.mailingNotifications.lists(appId),
        icon: <Icon icon='mdi:account-multiple' width={20} height={20} />,
        permissions: [P.NAV_MAILING],
      },
      {
        title: 'mailing',
        path: PATH_APP.application.mailingNotifications.mailing.list(appId),
        icon: <Icon icon='ci:mail' width={20} height={20} />,
        permissions: [P.NAV_MAILING],
      },
      {
        title: 'push campaigns',
        path: PATH_APP.application.mailingNotifications.campaign.list(appId),
        icon: <Icon icon='material-symbols:notifications-active-outline-rounded' width={20} height={20} />,
        permissions: [P.NAV_MAILING],
      },
      {
        title: 'push templates',
        path: PATH_APP.application.mailingNotifications.pushTemplate.list(appId),
        icon: <Icon icon='mdi:file-document-edit-outline' width={20} height={20} />,
        permissions: [P.NAV_MAILING],
      },
      {
        title: 'lifecycle flows',
        path: PATH_APP.application.mailingNotifications.lifecycle(appId),
        icon: <Icon icon='mdi:timeline-clock-outline' width={20} height={20} />,
        permissions: [P.NAV_MAILING],
      },
    ],
  },
  {
    title: 'webhook',
    path: PATH_APP.application.webhook.list(appId),
    icon: <Icon icon='mdi:webhook' width={20} height={20} />,
    permissions: [P.NAV_WEBHOOKS],
  },
  {
    title: 'consumption requests',
    path: PATH_APP.application.consumptionRequest.root(appId),
    icon: <Icon icon='mdi:package-variant-closed' width={20} height={20} />,
    children: [
      {
        title: 'Request List',
        path: PATH_APP.application.consumptionRequest.list(appId),
        icon: <Icon icon='mdi:list-box-outline' width={20} height={20} />,
        permissions: [P.NAV_CONSUMPTION],
      },
      {
        title: 'Approved Transactions',
        path: PATH_APP.application.consumptionRequest.approvedTransactions(appId),
        icon: <Icon icon='mdi:check-circle' width={20} height={20} />,
        permissions: [P.NAV_CONSUMPTION],
      },
    ],
    permissions: [P.NAV_CONSUMPTION],
  },
];

/** Pinned below main nav — sales channels, store URLs, legacy revenue settings */
const APPLICATION_SETTINGS_SECTION = (appId) => [
  {
    title: 'app settings',
    path: PATH_APP.application.settings(appId),
    icon: <Icon icon='mdi:cog-outline' width={20} height={20} />,
    permissions: [P.NAV_OVERVIEW],
  },
  {
    title: 'retention messaging',
    path: PATH_APP.application.retentionMessaging(appId),
    icon: <Icon icon='mdi:message-alert-outline' width={20} height={20} />,
    permissions: [P.NAV_RETENTION_MESSAGING],
  },
];

const managementRoutesForRole = (organizationRole) => {
  if (isMobeatStaff(organizationRole)) {
    return MOBEAT_STAFF_ROUTES.filter((route) => hasOrganizationPermission(organizationRole, route.permissions));
  }
  return MANAGEMENT_ROUTES.filter((route) => hasOrganizationPermission(organizationRole, route.permissions));
};

const sidebarConfig = ({ appId, organizationRole }) => ({
  applications: [
    {
      subheader: APPLICATIONS_MAP[appId] || '',
      items: filterApplicationRoutes(APPLICATION_ROUTES(appId).filter(Boolean), organizationRole),
    },
    {
      subheader: 'Settings',
      items: filterApplicationRoutes(APPLICATION_SETTINGS_SECTION(appId), organizationRole),
    },
  ].filter((section) => section.items?.length > 0),
  adminOverview: [
    {
      subheader: isMobeatStaff(organizationRole) ? 'Mobeat' : 'Management',
      items: managementRoutesForRole(organizationRole),
    },
  ],
});

export default sidebarConfig;
